import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Check,
  Clock,
  Plus,
  Video,
  Music,
  User,
  GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const taskTypeIcons = {
  visualizer: Video,
  music_video: Music,
  avatar_animation: User,
};

const taskTypeLabels = {
  visualizer: "Visualizer",
  music_video: "Music Video",
  avatar_animation: "Avatar Animation",
};

const columnConfig = [
  { id: "pending", title: "Pending", color: "text-meta-text-muted" },
  { id: "rendering", title: "Rendering", color: "text-meta-warning" },
  { id: "complete", title: "Complete", color: "text-meta-neon" },
];

const TaskCard = ({ task, onUpdateTask }) => {
  const Icon = taskTypeIcons[task.task_type] || Video;
  const isRendering = task.status === "rendering";
  const isComplete = task.status === "complete";

  const handleMoveToRendering = () => {
    onUpdateTask(task.id, { status: "rendering" });
    toast.info(`"${task.title}" moved to rendering queue`);
    // This triggers the trigger_render_cli hook
    console.log("RENDER_CLI_HOOK: Triggered for task", task.id);
  };

  const handleMarkComplete = () => {
    onUpdateTask(task.id, { status: "complete", progress: 100 });
    toast.success(`"${task.title}" marked as complete`);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-meta-elevated border border-meta-border p-4 group ${
        isRendering ? "animate-neon-pulse" : ""
      }`}
      data-testid={`task-card-${task.id}`}
    >
      {/* Drag Handle */}
      <div className="flex items-start gap-3">
        <GripVertical
          size={16}
          className="text-meta-text-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-grab mt-1"
        />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <Icon size={16} className="text-meta-neon shrink-0" />
            <span className="text-xs font-mono text-meta-text-muted uppercase">
              {taskTypeLabels[task.task_type]}
            </span>
          </div>

          {/* Title */}
          <h4 className="font-sans font-medium text-meta-text truncate mb-3">
            {task.title}
          </h4>

          {/* Progress (for rendering tasks) */}
          {isRendering && (
            <div className="mb-3">
              <div className="flex justify-between text-xs font-mono mb-1">
                <span className="text-meta-warning">Rendering...</span>
                <span className="text-meta-text-muted">{task.progress}%</span>
              </div>
              <Progress
                value={task.progress}
                className="h-1.5 bg-meta-base"
              />
            </div>
          )}

          {/* Actions */}
          {task.status === "pending" && (
            <Button
              onClick={handleMoveToRendering}
              size="sm"
              className="w-full bg-meta-neon text-meta-base hover:shadow-neon text-xs"
              data-testid={`start-render-${task.id}`}
            >
              <Play size={12} className="mr-1" />
              Start Render
            </Button>
          )}

          {isRendering && (
            <Button
              onClick={handleMarkComplete}
              size="sm"
              variant="outline"
              className="w-full border-meta-border text-meta-text hover:border-meta-neon hover:text-meta-neon text-xs"
              data-testid={`complete-task-${task.id}`}
            >
              <Check size={12} className="mr-1" />
              Mark Complete
            </Button>
          )}

          {isComplete && (
            <div className="flex items-center gap-2 text-meta-neon text-xs font-mono">
              <Check size={14} />
              <span>Completed</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const RenderQueue = ({ tasks, onUpdateTask, onCreateTask }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", task_type: "visualizer" });

  const getTasksByStatus = (status) => tasks.filter((t) => t.status === status);

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Please enter a task title");
      return;
    }

    const result = await onCreateTask(newTask);
    if (result.success) {
      toast.success("Task added to queue");
      setNewTask({ title: "", task_type: "visualizer" });
      setIsDialogOpen(false);
    } else {
      toast.error("Failed to create task");
    }
  };

  return (
    <div
      className="bg-meta-surface border border-meta-border"
      data-testid="render-queue"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Video className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Render Queue
            </h2>
            <p className="font-sans font-semibold text-meta-text">
              Content Pipeline
            </p>
          </div>
        </div>

        {/* Add Task Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-meta-border text-meta-text hover:border-meta-neon hover:text-meta-neon"
              data-testid="add-task-btn"
            >
              <Plus size={14} className="mr-1" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-meta-surface border-meta-border">
            <DialogHeader>
              <DialogTitle className="text-meta-text">Add Render Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <label className="text-meta-text-muted font-mono text-xs uppercase">
                  Task Title
                </label>
                <Input
                  value={newTask.title}
                  onChange={(e) =>
                    setNewTask((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="e.g., Track Name Visualizer"
                  className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon"
                  data-testid="new-task-title"
                />
              </div>
              <div className="space-y-2">
                <label className="text-meta-text-muted font-mono text-xs uppercase">
                  Task Type
                </label>
                <Select
                  value={newTask.task_type}
                  onValueChange={(value) =>
                    setNewTask((prev) => ({ ...prev, task_type: value }))
                  }
                >
                  <SelectTrigger className="bg-meta-base border-meta-border text-meta-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-meta-elevated border-meta-border">
                    <SelectItem value="visualizer">Visualizer</SelectItem>
                    <SelectItem value="music_video">Music Video</SelectItem>
                    <SelectItem value="avatar_animation">Avatar Animation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleCreateTask}
                className="w-full bg-meta-neon text-meta-base hover:shadow-neon"
                data-testid="create-task-btn"
              >
                Add to Queue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-3 gap-4 p-4">
        {columnConfig.map((column) => (
          <div key={column.id} className="space-y-3">
            {/* Column Header */}
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-2">
                {column.id === "pending" && <Clock size={14} className={column.color} />}
                {column.id === "rendering" && <Play size={14} className={column.color} />}
                {column.id === "complete" && <Check size={14} className={column.color} />}
                <span className={`font-mono text-xs uppercase ${column.color}`}>
                  {column.title}
                </span>
              </div>
              <span className="font-mono text-xs text-meta-text-muted">
                {getTasksByStatus(column.id).length}
              </span>
            </div>

            {/* Column Content */}
            <div className="min-h-[300px] bg-meta-base border border-meta-border-subtle p-2 space-y-2">
              <AnimatePresence>
                {getTasksByStatus(column.id).map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onUpdateTask={onUpdateTask}
                  />
                ))}
              </AnimatePresence>

              {getTasksByStatus(column.id).length === 0 && (
                <div className="h-full flex items-center justify-center text-meta-text-muted text-xs font-mono py-12">
                  No tasks
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
